// src/pages/Admin/AdminCategoryForm.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { categoriesAPI } from '../../api/categories.js';
import { useToast } from '../../hooks/useToast.js';
import { Button } from '../../components/ui/Button/Button.jsx';
import { Input } from '../../components/ui/Input/Input.jsx';

export const AdminCategoryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const isEdit = id && id !== 'new';
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    parent_id: '',
    sort_order: 0,
    is_active: true,
    icon: '',
    meta_title: '',
    meta_description: '',
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [categoriesRes] = await Promise.all([
        categoriesAPI.getAll({ is_active: true }),
      ]);
      setCategories(categoriesRes.data.data || []);

      if (isEdit) {
        const categoryRes = await categoriesAPI.getById(id);
        const category = categoryRes.data.data;
        setFormData({
          name: category.name || '',
          slug: category.slug || '',
          description: category.description || '',
          parent_id: category.parent_id || '',
          sort_order: category.sort_order || 0,
          is_active: category.is_active !== undefined ? category.is_active : true,
          icon: category.icon || '',
          meta_title: category.meta_title || '',
          meta_description: category.meta_description || '',
        });
      }
    } catch (err) {
      error('Не удалось загрузить данные');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.slug) {
      error('Название и Slug обязательны');
      return;
    }

    // Проверяем, не является ли parent_id самой категорией (защита от цикла)
    if (isEdit && formData.parent_id === id) {
      error('Категория не может быть родителем самой себя');
      return;
    }

    setIsLoading(true);
    try {
      const submitData = {
        ...formData,
        sort_order: parseInt(formData.sort_order) || 0,
        parent_id: formData.parent_id || null,
      };

      if (isEdit) {
        await categoriesAPI.update(id, submitData);
        success('Категория обновлена');
      } else {
        await categoriesAPI.create(submitData);
        success('Категория создана');
      }
      navigate('/admin/categories');
    } catch (err) {
      error(err.response?.data?.message || 'Не удалось сохранить категорию');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && isEdit) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>Загрузка...</div>;
  }

  return (
    <div style={{
      backgroundColor: 'rgb(var(--c-surface))',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <form onSubmit={handleSubmit}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '24px' }}>
          {isEdit ? 'Редактировать категорию' : 'Новая категория'}
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Input
            label="Название *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Slug *"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            required
          />
          <Input
            label="Описание"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <Input
            label="Иконка (CSS класс)"
            value={formData.icon}
            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
            placeholder="fa-font"
          />
        </div>

        <div style={{ height: '12px' }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
              Родительская категория
            </label>
            <select
              value={formData.parent_id}
              onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgb(var(--c-border))',
                fontSize: '14px',
                outline: 'none',
              }}
            >
              <option value="">Корневая (без родителя)</option>
              {categories
                .filter(cat => cat.id !== id) // Исключаем текущую категорию
                .map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
            </select>
          </div>
          <Input
            label="Порядок сортировки"
            type="number"
            value={formData.sort_order}
            onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
          />
        </div>

        <div style={{ height: '12px' }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Input
            label="SEO Заголовок"
            value={formData.meta_title}
            onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
          />
          <Input
            label="SEO Описание"
            value={formData.meta_description}
            onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
          />
        </div>

        <div style={{ height: '12px' }} />

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            />
            Активна
          </label>
        </div>

        <div style={{ height: '24px' }} />

        <div style={{ display: 'flex', gap: '12px' }}>
          <Button type="submit" isLoading={isLoading}>
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/admin/categories')}>
            Отмена
          </Button>
        </div>
      </form>
    </div>
  );
};